import clsx from "clsx";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import {
  MdConnectWithoutContact,
  MdNotifications,
  MdOutlineEditNotifications,
  MdOutlineFolderShared,
  MdOutlinePendingActions,
  MdOutlineRepeat,
} from "react-icons/md";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useTranslation } from "react-i18next";
import PageTitle from "../../components/PageTitle/PageTitle";
import {
  BUTTON_STYLES,
  ETransactionType,
  ICON_SIZES,
  NOTIFICATIONS_TABS,
} from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";

import useCurrentAccount from "../../hooks/useCurrentAccount";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import useCache from "../../hooks/useCache";
import useCurrentUser from "src/hooks/useCurrentUser";
import { AnimatePresence } from "framer-motion";
import TodayTotalsBar from "src/components/TodayTotalsBar/TodayTotalsBar";
import useCollapsibles from "src/hooks/useCollapsibles";
import { useEffect, useMemo, useState, useTransition } from "react";
import ScrollToSection from "src/components/ScrollToSection/ScrollToSection";
import RecurringTransactionsCollapsible, {
  ERecurringTransactionType,
} from "src/components/RecurringTransaction/RecurringTransactionsCollapsible";
import useAllTransactions from "src/hooks/useAllTransactions";
import TransactionsPendingList from "src/components/TransactionsPendingList/TransactionsPendingList";
import LinksList from "src/components/LinksList/LinksList";
import useRecurringTransactions from "src/hooks/useRecurringTransactions";
import userPreferencesStore from "src/store/userPreferences";
import NoTableData from "src/components/NoTableData/NoTableData";
import useDarkMode from "src/hooks/useDarkMode";
import ReceivedInvitesList from "src/components/ReceivedInvitesList/ReceivedInvitesList";
import useInvites from "src/hooks/useInvites";
import PushSubscription from "src/components/PushSubscription/PushSubscription";
import NotificationSettings from "src/components/NotificationSettings/NotificationSettings";
import ReceivedTransactionsList from "src/components/ReceivedTransactionsList/ReceivedTransactionsList";
import useUsers from "src/hooks/useUsers";
import MarqueeRow from "src/components/MarqueeRow/MarqueeRow";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";

const NotificationsPage: React.FC = () => {
  const [isPending, startTransition] = useTransition();

  const { isEnabled: isDarkMode } = useDarkMode();
  const { isLoadingUsers } = useUsers();
  const { receivedInvites } = useInvites();
  const [canScroll, setCanScroll] = useState(false);
  const { t } = useTranslation();
  const { notificationsActiveTab, setNotificationsActiveTab } =
    userPreferencesStore();
  const [activeTab, setActiveTab] = useState<number>(notificationsActiveTab);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);

  const { isLoadingAccounts, enabledAccountsList } = useCurrentUser();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const {
    isLoadingAllTransactions,
    allPendingTransactions,
    allReceivedTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
  } = useAllTransactions();
  const { upcomingPayments, recentOverduePayments, todayPayments } =
    useRecurringTransactions(false);

  const totalRecurring = useMemo(() => {
    return (
      upcomingPayments?.length +
      recentOverduePayments?.length +
      todayPayments?.length
    );
  }, [upcomingPayments, recentOverduePayments, todayPayments]);

  const { currentAccountData, activeTransactions, currencySymbol } =
    useCurrentAccount();
  const {
    collapsiblePreferences,
    updateCollapsible,
    isRecurringTransactionsMobileCollapsibleOpen,
    toggleRecurringTransactionsMobileCollapsible,
  } = useCollapsibles();

  const { isMobile } = useMobile();
  const { clearCache } = useCache();

  useEffect(() => {
    startTransition(() => setNotificationsActiveTab(activeTab));
  }, [activeTab]);

  const isLoadingSomething = useMemo(() => {
    return [
      !!isLoadingAccounts,
      !!isLoadingAllTransactions,
      !!isLoadingUsers,
      !!isLoadingCustomizedCategories,
      !!isLoadingCustomizedSources,
    ]?.includes(true);
  }, [
    isLoadingAccounts,
    isLoadingAllTransactions,
    isLoadingUsers,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
  ]);

  const mobileMenuMarquee = useMemo(() => {
    switch (notificationsActiveTab) {
      case NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS: {
        return "Notificaciones de transacciones recurrentes";
      }
      case NOTIFICATIONS_TABS.PENDING_TRANSACTIONS: {
        return "Notificaciones de transacciones pendientes";
      }
      case NOTIFICATIONS_TABS.MY_NETWORK: {
        return "Notificaciones de solicitudes de contacto";
      }
      case NOTIFICATIONS_TABS.SHARED_TRANSACTIONS: {
        return "Notificaciones de transacciones recibidas";
      }
      default: {
        return null;
      }
    }
  }, [notificationsActiveTab]);

  useEffect(() => {
    if (!isMobile) {
      setShowSettingsModal(false);
    }
  }, [isMobile]);

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          isLoadingSomething ? null : isMobile ? null : (
            <NotificationSettings showSubscriptionMessage />
          )
        }
        mobileMenuChildren={<MarqueeRow text={mobileMenuMarquee} />}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            <>
              {isLoadingSomething ? (
                <LoadingWithTextPage />
              ) : (
                <>
                  <PageTitle
                    icon={<MdNotifications size={ICON_SIZES.MD} />}
                    title={t("Notificaciones")}
                    subtitle={isMobile ? "Todas las cuentas" : null}
                    rightSide={
                      <>
                        <ButtonGroup
                          buttons={[
                            {
                              hide: !isMobile,
                              className: isMobile ? BUTTON_STYLES.HEADING : "",
                              text: "",
                              onClick: () => setShowSettingsModal(true),
                              icon: (
                                <MdOutlineEditNotifications
                                  size={ICON_SIZES.MD}
                                />
                              ),
                            },
                            generalOptionsDropdown,
                          ]}
                          gap={1}
                        />
                      </>
                    }
                  />
                  <TodayTotalsBar />

                  <div
                    className={clsx(["flex", "flex-col", "gap-4", "w-full"])}
                    id="focused-section"
                  >
                    <LinksList
                      links={[
                        {
                          text: t("Transacciones recurrentes"),
                          onClick: () => {
                            setActiveTab(
                              NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS
                            );
                          },
                          active:
                            activeTab ===
                            NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS,
                          icon: <MdOutlineRepeat size={ICON_SIZES.MD_LG} />,
                          count: totalRecurring,
                          showCount: !!totalRecurring,
                          isLoading:
                            activeTab ===
                              NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS &&
                            isPending,
                        },
                        {
                          text: t("Transacciones pendientes"),
                          onClick: () => {
                            setActiveTab(
                              NOTIFICATIONS_TABS.PENDING_TRANSACTIONS
                            );
                          },
                          active:
                            activeTab ===
                            NOTIFICATIONS_TABS.PENDING_TRANSACTIONS,
                          icon: (
                            <MdOutlinePendingActions size={ICON_SIZES.MD_LG} />
                          ),
                          count: allPendingTransactions?.length,
                          showCount: !!allPendingTransactions?.length,
                          isLoading:
                            activeTab ===
                              NOTIFICATIONS_TABS.PENDING_TRANSACTIONS &&
                            isPending,
                        },
                        {
                          text: t("Solicitudes de contacto"),
                          onClick: () => {
                            setActiveTab(NOTIFICATIONS_TABS.MY_NETWORK);
                          },
                          active: activeTab === NOTIFICATIONS_TABS.MY_NETWORK,
                          icon: (
                            <MdConnectWithoutContact size={ICON_SIZES.MD_LG} />
                          ),
                          count: receivedInvites?.length,
                          showCount: !!receivedInvites?.length,
                          isLoading:
                            activeTab === NOTIFICATIONS_TABS.MY_NETWORK &&
                            isPending,
                        },
                        {
                          text: t("Transacciones recibidas"),
                          onClick: () => {
                            setActiveTab(
                              NOTIFICATIONS_TABS.SHARED_TRANSACTIONS
                            );
                          },
                          active:
                            activeTab ===
                            NOTIFICATIONS_TABS.SHARED_TRANSACTIONS,
                          icon: (
                            <MdOutlineFolderShared size={ICON_SIZES.MD_LG} />
                          ),
                          count: allReceivedTransactions?.length,
                          showCount: !!allReceivedTransactions?.length,
                          isLoading:
                            activeTab ===
                              NOTIFICATIONS_TABS.SHARED_TRANSACTIONS &&
                            isPending,
                        },
                      ]}
                    />

                    <PushSubscription hideIfEnabled />

                    {/* {isPending ? (
                      <LoadingWithTextPage />
                    ) : ( */}
                    <>
                      {activeTab ===
                        NOTIFICATIONS_TABS.RECURRING_TRANSACTIONS && (
                        <>
                          {totalRecurring === 0 ? (
                            <NoTableData
                              className={clsx(["!mt-12", "md:!mt-16"])}
                            />
                          ) : (
                            <>
                              <RecurringTransactionsCollapsible
                                title="Hoy"
                                type={ERecurringTransactionType.TODAY}
                                onlyCurrentAccount={false}
                              />
                              <RecurringTransactionsCollapsible
                                title="Atrasadas"
                                type={ERecurringTransactionType.OVERDUE}
                                onlyCurrentAccount={false}
                              />
                              <RecurringTransactionsCollapsible
                                title="Próximas"
                                type={ERecurringTransactionType.UPCOMING}
                                onlyCurrentAccount={false}
                              />
                            </>
                          )}
                        </>
                      )}

                      {activeTab ===
                        NOTIFICATIONS_TABS.PENDING_TRANSACTIONS && (
                        <>
                          {allPendingTransactions?.length === 0 ? (
                            <NoTableData
                              className={clsx(["!mt-12", "md:!mt-16"])}
                            />
                          ) : (
                            <>
                              <TransactionsPendingList
                                type={ETransactionType.EXPENSE}
                                onlyCurrentAccount={false}
                                columns={2}
                              />

                              <TransactionsPendingList
                                type={ETransactionType.INCOME}
                                onlyCurrentAccount={false}
                                columns={2}
                              />
                            </>
                          )}
                        </>
                      )}

                      {activeTab === NOTIFICATIONS_TABS.MY_NETWORK && (
                        <>
                          {receivedInvites?.length ? (
                            <ReceivedInvitesList columns={2} />
                          ) : (
                            <NoTableData
                              className={clsx(["!mt-12", "md:!mt-16"])}
                            />
                          )}
                        </>
                      )}

                      {activeTab === NOTIFICATIONS_TABS.SHARED_TRANSACTIONS && (
                        <>
                          {allReceivedTransactions?.length ? (
                            <ReceivedTransactionsList columns={1} />
                          ) : (
                            <NoTableData
                              className={clsx(["!mt-12", "md:!mt-16"])}
                            />
                          )}
                        </>
                      )}
                    </>
                    {/* )} */}
                  </div>
                  <ScrollToSection
                    targetId="focused-section"
                    enabled={canScroll}
                  />
                </>
              )}
            </>
          </AnimatePresence>
        </div>
      </TwoSidebars>
      {showSettingsModal && (
        <NotificationSettings
          isModal
          showSubscriptionMessage
          onModalClose={() => setShowSettingsModal(false)}
        />
      )}
    </>
  );
};

export default NotificationsPage;
